import React, { useState, useEffect } from 'react';
import './AddMaterialTypeModal.css';

const AddMaterialTypeModal = ({ open, onClose, onMaterialTypeAdded, editMaterialType = null, supplierId = null, supplierName = null }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [formData, setFormData] = useState({
    productCode: '',
    topsheet: '',
    fscType: '',
    adhesive: '',
    backing: '',
    jumboWidth: '',
    pricePerSqm: '',
    supplierId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch suppliers when modal opens
  useEffect(() => {
    if (open) {
      fetchSuppliers();
      if (editMaterialType) {
        setFormData({
          productCode: editMaterialType.productCode,
          topsheet: editMaterialType.topsheet,
          fscType: editMaterialType.fscType || '',
          adhesive: editMaterialType.adhesive,
          backing: editMaterialType.backing,
          jumboWidth: editMaterialType.jumboWidth || '',
          pricePerSqm: editMaterialType.pricePerSqm,
          supplierId: editMaterialType.supplierId
        });
      } else {
        setFormData({
          productCode: '',
          topsheet: '',
          fscType: '',
          adhesive: '',
          backing: '',
          jumboWidth: '',
          pricePerSqm: '',
          supplierId: supplierId || ''
        });
      }
      setError('');
    }
  }, [open, editMaterialType, supplierId]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/suppliers`);
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.productCode.trim()) {
      setError('Product code is required');
      return;
    }
    if (!formData.topsheet.trim()) {
      setError('Topsheet is required');
      return;
    }
    if (!formData.adhesive.trim()) {
      setError('Adhesive is required');
      return;
    }
    if (!formData.backing.trim()) {
      setError('Backing is required');
      return;
    }
    if (!formData.pricePerSqm || parseFloat(formData.pricePerSqm) <= 0) {
      setError('Valid price per square meter is required');
      return;
    }
    if (!formData.supplierId) {
      setError('Supplier selection is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = editMaterialType 
        ? `${process.env.REACT_APP_API_URL || ''}/material-types/${editMaterialType.id}`
        : `${process.env.REACT_APP_API_URL || ''}/material-types`;
      
      const method = editMaterialType ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save material type');
      }

      const materialType = await response.json();
      
      if (onMaterialTypeAdded) {
        onMaterialTypeAdded(materialType);
      }
      
      onClose();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content material-type-modal">
        <div className="modal-header">
          <h2>{editMaterialType ? '✏️ Edit Material Type' : `📋 Add Material Type${supplierName ? ` - ${supplierName}` : ''}`}</h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={loading}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="productCode">
                Product Code <span className="required">*</span>
              </label>
              <input
                type="text"
                id="productCode"
                name="productCode"
                value={formData.productCode}
                onChange={handleInputChange}
                placeholder="e.g., PP-WH-AC-PET"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="supplierId">
                Supplier <span className="required">*</span>
              </label>
              <select
                id="supplierId"
                name="supplierId"
                value={formData.supplierId}
                onChange={handleInputChange}
                required
                disabled={loading || !!supplierId}
              >
                <option value="">Select Supplier</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="topsheet">
                Topsheet <span className="required">*</span>
              </label>
              <input
                type="text"
                id="topsheet"
                name="topsheet"
                value={formData.topsheet}
                onChange={handleInputChange}
                placeholder="e.g., White PP, Clear PET"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="fscType">FSC Type</label>
              <select
                id="fscType"
                name="fscType"
                value={formData.fscType}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">No FSC</option>
                <option value="FSC Mix">FSC Mix</option>
                <option value="FSC Recycled">FSC Recycled</option>
                <option value="FSC 100%">FSC 100%</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="adhesive">
                Adhesive <span className="required">*</span>
              </label>
              <input
                type="text"
                id="adhesive"
                name="adhesive"
                value={formData.adhesive}
                onChange={handleInputChange}
                placeholder="e.g., Acrylic, Hotmelt"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="backing">
                Backing <span className="required">*</span>
              </label>
              <input
                type="text"
                id="backing"
                name="backing"
                value={formData.backing}
                onChange={handleInputChange}
                placeholder="e.g., Paper, Film"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="jumboWidth">
                Jumbo Width (mm) <span className="required">*</span>
              </label>
              <input
                type="number"
                id="jumboWidth"
                name="jumboWidth"
                min="1"
                value={formData.jumboWidth}
                onChange={handleInputChange}
                placeholder="e.g., 1320"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricePerSqm">
                Price per Square Meter (R) <span className="required">*</span>
              </label>
              <input
                type="number"
                id="pricePerSqm"
                name="pricePerSqm"
                step="0.01"
                min="0"
                value={formData.pricePerSqm}
                onChange={handleInputChange}
                placeholder="0.00"
                required
                disabled={loading}
              />
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (editMaterialType ? 'Update Material Type' : 'Add Material Type')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMaterialTypeModal;
